import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Alert, Badge, Button, Card, Form, Modal } from 'react-bootstrap';

import { Link } from 'react-router-dom';

import {

  canOpenTransportInfraGate,

  infraImplementationGateSubtasks,

  partitionInfraGateSubtasks,

  areaHasActiveImplementationGate,
  externalIncidentWorkGroups,
  transportImplementGateEligibleAreas,

} from '../changeInfraIncidentAreas';

import {

  canCancelGateSubtask,

  gateSubtaskCardBorderClass,

  gateSubtaskStatusLabel,

  gateSubtaskWaitHint,

} from '../changeGateSubtaskDisplay';

import { useModal } from '../../../context/ModalContext';

import type { ChangeSubtask } from '../../../types/changes';

import {

  emptyIncidentDraft,

  incidentAllowsAttachments,

  incidentCustomFieldsPayload,

  type IncidentSendDraft,

} from '../incidentQuickSend';

import { changesApi } from '../../../services/changesApi';

import type { ChangeRequest, ChangeWorkGroup } from '../../../types/changes';

import { getApiErrorMessage } from '../../../utils/apiError';

import { validateAttachmentFile } from '../../../utils/uploadValidation';

import { validateIncidentTicketFields, uploadIncidentCustomFieldFiles } from '../incidentTicketFields';

import { ASSIGNMENT_SETOR } from '../changeSubtaskAssignment';

import { useActiveUsers } from '../hooks/useActiveUsers';

import IncidentSendPackageForm from './IncidentSendPackageForm';

import IncidentTicketTemplatePicker from './IncidentTicketTemplatePicker';

import { useAuth } from '../../../context/AuthContext';



export type InfraGateOpenButtonState = {
  visible: boolean;
  open: () => void;
};

interface Props {
  change: ChangeRequest;
  onUpdated: () => Promise<void> | void;
  /** Botão primário renderizado na barra da aba Transporte (pai). */
  delegateOpenButton?: boolean;
  onOpenButtonState?: (state: InfraGateOpenButtonState | null) => void;
}



const ChangeInfraGateDispatchPanel: React.FC<Props> = ({
  change,
  onUpdated,
  delegateOpenButton = false,
  onOpenButtonState,
}) => {

  const { user } = useAuth();

  const [areas, setAreas] = useState<ChangeWorkGroup[]>([]);

  const [workflows, setWorkflows] = useState<

    Array<{ id: number; name: string; group_ids: number[] }>

  >([]);

  const [error, setError] = useState<string | null>(null);

  const [info, setInfo] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);

  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);

  const [draft, setDraft] = useState<IncidentSendDraft | null>(null);

  const [sending, setSending] = useState(false);

  const [cancelingId, setCancelingId] = useState<number | null>(null);

  const { showConfirm } = useModal();

  const { users: incidentPeople } = useActiveUsers({ limit: 80, enabled: modalOpen });



  const loadAreas = useCallback(async () => {

    try {

      const [groupsRes, targets] = await Promise.all([

        changesApi.listWorkGroups({ only_active: true }),

        changesApi.listIncidentTargets(),

      ]);

      setAreas(groupsRes.items || []);

      setWorkflows(targets.workflows || []);

    } catch {

      setAreas([]);

      setWorkflows([]);

    }

  }, []);



  useEffect(() => {

    void loadAreas();

  }, [loadAreas]);



  const eligible = useMemo(

    () => transportImplementGateEligibleAreas(areas, change),

    [areas, change],

  );

  const areaOptions = useMemo<ChangeWorkGroup[]>(
    () => externalIncidentWorkGroups(areas),
    [areas],
  );

  const gateSubtaskPartition = useMemo(() => partitionInfraGateSubtasks(change), [change]);

  const existingGateSubtasks = gateSubtaskPartition.active;

  const gateSubtaskHistory = gateSubtaskPartition.history;



  const mayOpenGate = canOpenTransportInfraGate(change, user);

  const canDispatch = useMemo(() => {

    const policy = change.change_type_policy;

    const externalOk = policy?.enable_external_subtasks !== false;

    return externalOk && mayOpenGate && eligible.length > 0;

  }, [change, eligible.length, mayOpenGate]);



  const selectedGroup: ChangeWorkGroup | null =
    areaOptions.find((item: ChangeWorkGroup) => item.group_id === selectedGroupId) ||
    areaOptions[0] ||
    null;

  const defaultAreaGroupId = useMemo(() => {
    if (eligible.length) return eligible[0].group_id;
    const open = areaOptions.find((item) => !areaHasActiveImplementationGate(change, item.group_id));
    return open?.group_id ?? areaOptions[0]?.group_id ?? null;
  }, [areaOptions, change, eligible]);

  useEffect(() => {
    if (!areaOptions.length) {
      setSelectedGroupId(null);
      return;
    }
    setSelectedGroupId((prev) => {
      if (prev && areaOptions.some((item) => item.group_id === prev)) return prev;
      return defaultAreaGroupId;
    });
  }, [areaOptions, defaultAreaGroupId]);

  const patchDraft = (patch: Partial<IncidentSendDraft>) => {
    if (!selectedGroup) return;
    setDraft((prev) => ({
      ...(prev || emptyIncidentDraft(selectedGroup, { blankWorkflow: true })),
      ...patch,
    }));
  };

  const handleChangeArea = (groupId: number) => {
    const group = areaOptions.find((item) => item.group_id === groupId);
    if (!group) return;
    setSelectedGroupId(groupId);
    setDraft(emptyIncidentDraft(group, { blankWorkflow: true }));
    setError(null);
  };

  const openModal = useCallback(() => {
    if (!areaOptions.length) return;
    const group =
      areaOptions.find((item) => item.group_id === defaultAreaGroupId) || areaOptions[0];
    if (!group) return;
    setSelectedGroupId(group.group_id);
    setDraft(emptyIncidentDraft(group, { blankWorkflow: true }));
    setError(null);
    setModalOpen(true);
  }, [areaOptions, defaultAreaGroupId]);



  const handleSend = async () => {

    if (!selectedGroup || !draft) return;

    if (areaHasActiveImplementationGate(change, selectedGroup.group_id)) {
      setError('Esta área já possui chamado externo de gate de Implementar nesta mudança.');
      return;
    }

    if (!draft.workflow_id) {

      setError('Escolha o fluxo de incidentes.');

      return;

    }

    if (!draft.incidents_group_id) {

      setError('Escolha o grupo para encaminhar o chamado.');

      return;

    }

    if (!draft.title.trim()) {

      setError('Informe o título do chamado.');

      return;

    }

    if (!draft.comments.trim()) {

      setError('Informe a descrição do chamado.');

      return;

    }

    if (!draft.urgency) {

      setError('Informe a urgência do chamado.');

      return;

    }

    if (!incidentAllowsAttachments(selectedGroup) && draft.files.length) {

      setError('Este chamado não aceita anexo.');

      return;

    }

    const customError = validateIncidentTicketFields(

      draft.customFields,

      draft.customFieldValues,

      draft.customFieldFiles,

    );

    if (customError) {

      setError(customError);

      return;

    }

    const invalid = draft.files.map(validateAttachmentFile).find(Boolean);

    if (invalid) {

      setError(invalid);

      return;

    }

    setError(null);

    setSending(true);

    let createdSubtaskId: number | null = null;

    try {

      const created = await changesApi.createSubtask(change.id, {

        title: draft.title.trim(),

        group_id: selectedGroup.group_id,

        comments: draft.comments.trim(),

        assignment_kind: ASSIGNMENT_SETOR,

        urgency: draft.urgency,

        workflow_id: Number(draft.workflow_id),

        incidents_group_id: draft.incidents_group_id,

        has_attachment: draft.files.length > 0,

        gates_implementar: true,

        ...incidentCustomFieldsPayload(draft),

      });

      createdSubtaskId = created.id;

      for (const file of draft.files) {

        const formData = new FormData();

        formData.append('file', file);

        await changesApi.uploadSubtaskAttachment(change.id, created.id, formData);

      }

      if (created.ticket_id) {

        await uploadIncidentCustomFieldFiles(created.ticket_id, draft.customFieldFiles);

      }

      setInfo(

        created.ticket_id

          ? `Subtarefa externa criada. Ticket #${created.ticket_id}.`

          : 'Subtarefa externa criada.',

      );

      setModalOpen(false);

      await onUpdated();

    } catch (err: unknown) {

      if (createdSubtaskId != null) {

        await onUpdated();

        setError(

          `${getApiErrorMessage(err, 'Não foi possível concluir anexos ou campos de arquivo.')}` +

            ' A subtarefa e o chamado já foram criados — confira a lista abaixo ou em Subtarefas.',

        );

      } else {

        setError(getApiErrorMessage(err, 'Não foi possível abrir o chamado externo.'));

      }

    } finally {

      setSending(false);

    }

  };



  const gateStatusBadge = (subtask: ChangeSubtask) => {

    const label = gateSubtaskStatusLabel(subtask);

    if (label === 'Concluída') return <Badge bg="success">{label}</Badge>;

    if (label === 'Encerrada sem sucesso') return <Badge bg="danger">{label}</Badge>;

    if (label === 'Em atendimento') return <Badge bg="primary">{label}</Badge>;

    return <Badge bg="warning" text="dark">{label}</Badge>;

  };



  const handleCancelGate = async (subtask: ChangeSubtask) => {

    const ok = await showConfirm({

      title: 'Cancelar chamado externo',

      message:

        subtask.ticket_id != null

          ? `Remover a subtarefa "${subtask.title}"? O chamado #${subtask.ticket_id} no Incidente permanece; somente gestor de mudanças pode excluir subtarefa com ticket aberto.`

          : `Remover a subtarefa externa "${subtask.title}"? Você poderá abrir outra para a mesma área.`,

      confirmLabel: 'Sim, cancelar',

      cancelLabel: 'Não cancelar',

    });

    if (!ok) return;

    setError(null);

    setCancelingId(subtask.id);

    try {

      await changesApi.deleteSubtask(change.id, subtask.id);

      setInfo('Chamado externo de gate removido.');

      await onUpdated();

    } catch (err: unknown) {

      setError(getApiErrorMessage(err, 'Não foi possível cancelar a subtarefa externa.'));

    } finally {

      setCancelingId(null);

    }

  };



  const inTransporte = change.status === 'transporte';

  const inImplementarCatchUp = change.status === 'implementar';

  const canOpenNewGate = inTransporte || inImplementarCatchUp;

  const showInlineOpenButton =
    !delegateOpenButton && canOpenNewGate && mayOpenGate && canDispatch;

  useEffect(() => {
    if (!delegateOpenButton || !onOpenButtonState) return;
    const visible = Boolean(canOpenNewGate && mayOpenGate && canDispatch);
    onOpenButtonState(visible ? { visible: true, open: openModal } : null);
    return () => onOpenButtonState(null);
  }, [
    delegateOpenButton,
    onOpenButtonState,
    canOpenNewGate,
    mayOpenGate,
    canDispatch,
    openModal,
  ]);

  if (!canOpenNewGate && !existingGateSubtasks.length && !gateSubtaskHistory.length) {

    return null;

  }



  return (

    <div className="mb-3">

      {error ? (

        <Alert variant="danger" dismissible onClose={() => setError(null)}>

          {error}

        </Alert>

      ) : null}

      {info ? (

        <Alert variant="success" dismissible onClose={() => setInfo(null)}>

          {info}

        </Alert>

      ) : null}

      {showInlineOpenButton ? (
        <Button variant="primary" className="mb-3" onClick={openModal}>
          Abrir subtarefa externa
        </Button>
      ) : canOpenNewGate &&

        mayOpenGate &&

        eligible.length === 0 &&

        !existingGateSubtasks.length ? (

        <Alert variant="light" className="mb-3 small">

          {changeHasGateMessage(change, user, eligible.length, areas)}

        </Alert>

      ) : null}

      {existingGateSubtasks.length || gateSubtaskHistory.length ? (

        <div className="mb-4">

          <h6 className="mb-2">Chamados externos de gate (Implementar → Revisar)</h6>

          {[...existingGateSubtasks, ...gateSubtaskHistory].map((subtask) => (

            <Card

              key={subtask.id}

              className={gateSubtaskCardBorderClass(subtask)}

              data-testid={`gate-subtask-card-${subtask.id}`}

            >

              <Card.Body>

                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">

                  <div>

                    <strong>{subtask.group_name || subtask.title}</strong>

                    {subtask.group_name ? (

                      <div className="small text-muted">{subtask.title}</div>

                    ) : null}

                    {subtask.ticket_id ? (

                      <div className="small mt-1">

                        <Link to={`/tickets/${subtask.ticket_id}`}>Chamado #{subtask.ticket_id}</Link>

                        <span className="text-muted ms-2">· Subtarefa #{subtask.id}</span>

                      </div>

                    ) : (

                      <div className="small text-muted mt-1">Subtarefa #{subtask.id}</div>

                    )}

                  </div>

                  {gateStatusBadge(subtask)}

                </div>

                <p className="small text-muted mb-2">
                  {subtask.invalidation_reason || gateSubtaskWaitHint(subtask)}
                </p>

                {canOpenNewGate &&
                !subtask.invalidated_at &&
                canCancelGateSubtask(subtask, change, user) ? (

                  <Button

                    size="sm"

                    variant="outline-danger"

                    disabled={cancelingId !== null || sending}

                    onClick={() => void handleCancelGate(subtask)}

                  >

                    {cancelingId === subtask.id ? 'Cancelando…' : 'Cancelar'}

                  </Button>

                ) : null}

              </Card.Body>

            </Card>

          ))}

        </div>

      ) : null}

      <Modal show={modalOpen} onHide={() => setModalOpen(false)} size="lg" backdrop="static">

        <Modal.Header closeButton>

          <Modal.Title>Abrir subtarefa externa</Modal.Title>

        </Modal.Header>

        <Modal.Body>

          {areaOptions.length ? (
            <Form.Group className="mb-3">
              <Form.Label>Área na mudança</Form.Label>
              <Form.Select
                aria-label="Área na mudança"
                value={selectedGroupId ?? ''}
                onChange={(e) => handleChangeArea(Number(e.target.value))}
              >
                {areaOptions.map((group) => {
                  const gateOpen = areaHasActiveImplementationGate(change, group.group_id);
                  return (
                    <option key={group.group_id} value={group.group_id} disabled={gateOpen}>
                      {group.name}
                      {gateOpen ? ' (chamado de gate já aberto)' : ''}
                    </option>
                  );
                })}
              </Form.Select>
              <Form.Text className="text-muted">
                Todas as áreas externas do cadastro. Áreas com gate ativo ficam desabilitadas.
              </Form.Text>
            </Form.Group>
          ) : null}

          {selectedGroup && draft ? (

            <>

              {inImplementarCatchUp ? (

                <IncidentTicketTemplatePicker

                  group={selectedGroup}

                  workflows={workflows}

                  onApply={(patch, notice) => {

                    patchDraft(patch);

                    if (notice) setInfo(notice);

                  }}

                  onError={setError}

                />

              ) : null}

              <IncidentSendPackageForm

                group={selectedGroup}

                draft={draft}

                workflows={workflows}

                users={incidentPeople}

                onPatch={patchDraft}

              />

            </>

          ) : null}

        </Modal.Body>

        <Modal.Footer>

          <Button variant="outline-secondary" onClick={() => setModalOpen(false)} disabled={sending}>

            Cancelar

          </Button>

          <Button variant="primary" onClick={() => void handleSend()} disabled={sending}>

            {sending ? 'Enviando…' : 'Abrir chamado no Incidente'}

          </Button>

        </Modal.Footer>

      </Modal>

    </div>

  );

};



function changeHasGateMessage(

  change: ChangeRequest,

  user: { id?: number } | null | undefined,

  eligibleCount: number,

  areas: ChangeWorkGroup[],

): string {

  if (!eligibleCount && (change.subtasks || []).some((item) => item.gates_implementar)) {

    return 'Todas as áreas disponíveis já têm chamado externo de gate nesta mudança.';

  }

  if (!eligibleCount) {

    const externals = areas.filter((a) => a.open_in_incidents && a.incidents_group_id);

    if (!externals.length) {

      return 'Nenhum grupo com “Abre chamado no Incidente” ativo. Configure em Cadastros → Grupos → Externa.';

    }

    return 'Nenhum grupo externo disponível para esta mudança.';

  }

  return 'Não é possível abrir o chamado neste momento.';

}



export default ChangeInfraGateDispatchPanel;

